import {
  ProcessExecutionType,
  ProcessFlowchartNodeDetailsType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {RouterConfig} from 'processes/modals/router-config/RouterConfigConstants';
import {
  ProcessFlowchartEdgeType,
  TransitionSource,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {TaskDefinitionVersionSpecWithTaskSchema} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';

export interface ProcessFlowchartForValidation {
  id: string;
  nodes: Array<ProcessNodeForValidation>;
  edges: Array<ProcessEdgeForValidation>;
}

export interface ProcessEdgeForValidationBase {
  id: string;
  source: string;
  target: string;
  type: ProcessFlowchartEdgeType;
}

interface ControlFlowEdgeForValidation extends ProcessEdgeForValidationBase {
  type: ProcessFlowchartEdgeType.CONTROL_FLOW;
}

export interface DataFlowEdgeForValidation extends ProcessEdgeForValidationBase {
  type: ProcessFlowchartEdgeType.DATA_FLOW;
  sourceHandle: string;
  targetHandle: string;
  isLookupConfigured: boolean;
  transitionSource?: TransitionSource;
}

export type ProcessEdgeForValidation = ControlFlowEdgeForValidation | DataFlowEdgeForValidation;

export interface BaseNodeForValidation {
  id: string;
  name: string;
  nodeType: ProcessFlowchartNodeDetailsType;
}

export interface BaseNodeWithSchema extends BaseNodeForValidation {
  taskSchemaId: string;
  taskDefinitionVersionSpec: TaskDefinitionVersionSpecWithTaskSchema;
  executionType: ProcessExecutionType;
}

export interface ProcessRootNodeForValidation extends BaseNodeWithSchema {
  nodeType: ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS;
}

export interface ProcessOutputNodeForValidation extends BaseNodeWithSchema {
  nodeType: ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS;
}

export interface ProcessTaskNodeForValidation extends BaseNodeWithSchema {
  nodeType: ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS;
}

export type ProcessTaskSchemaNodeForValidation =
  | ProcessTaskNodeForValidation
  | ProcessRootNodeForValidation
  | ProcessOutputNodeForValidation;

export interface ProcessRouterNodeForValidation extends BaseNodeForValidation {
  routerConfig: RouterConfig;
  nodeType: ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS;
}

export interface ProcessEndNodeForValidation extends BaseNodeForValidation {
  nodeType: ProcessFlowchartNodeDetailsType.END_NODE_DETAILS;
}

export type ProcessNodeForValidation =
  | ProcessTaskSchemaNodeForValidation
  | ProcessRouterNodeForValidation
  | ProcessEndNodeForValidation;

/**
 * Error reporting - keep in sync with benchling/processes/api/utils/flowchart_validation/constants.py
 */

export enum ProcessFlowchartObjectType {
  NODE = 'NODE',
  EDGE = 'EDGE',
  HANDLE = 'HANDLE',
  ROW = 'ROW',
  UNKNOWN = 'UNKNOWN',
  // These errors are associated with the overall flowchart config and show up in a banner at the top of the editor
  FLOWCHART_CONFIG = 'FLOWCHART_CONFIG',
}

interface ProcessFlowchartErrorObject {
  id: string;
  type: ProcessFlowchartObjectType;
}

export enum ProcessFlowchartErrorType {
  NODE_NOT_AVAILABLE = 'NODE_NOT_AVAILABLE',
  NODE_MISSING_REQUIRED_PATH = 'NODE_MISSING_REQUIRED_PATH',
  NODE_NOT_CONNECTED = 'NODE_NOT_CONNECTED',
  TASK_NODE_NOT_CONNECTED = 'TASK_NODE_NOT_CONNECTED',
  OUTPUT_NODE_NOT_CONNECTED = 'OUTPUT_NODE_NOT_CONNECTED',
  ROOT_NODE_NOT_CONNECTED = 'ROOT_NODE_NOT_CONNECTED',
  ROUTER_NOT_CONNECTED = 'ROUTER_NOT_CONNECTED',
  END_NODE_NOT_CONNECTED = 'END_NODE_NOT_CONNECTED',
  END_NODE_CONNECTED_TO_NON_ROUTER_NODE = 'END_NODE_CONNECTED_TO_NON_ROUTER_NODE',
  END_NODE_CONNECTED_TO_MULTIPLE_SOURCE_NODES = 'END_NODE_CONNECTED_TO_MULTIPLE_SOURCE_NODES',
  END_NODE_HAS_OUTBOUND_EDGES = 'END_NODE_HAS_OUTBOUND_EDGES',
  NO_FLOWCHART_TASK_NODE = 'NO_FLOWCHART_TASK_NODE',
  NO_INTERMEDIATE_NODES = 'NO_INTERMEDIATE_NODES',
  TOO_MANY_START_NODES = 'TOO_MANY_START_NODES',
  NO_OUTPUT_NODE = 'NO_OUTPUT_NODE',
  EDGE_CONNECTS_UNAVAILABLE_NODES = 'EDGE_CONNECTS_UNAVAILABLE_NODES',
  INVALID_EDGE_BETWEEN_SCHEMAS = 'INVALID_EDGE_BETWEEN_SCHEMAS',
  EDGE_NOT_AVAILABLE = 'EDGE_NOT_AVAILABLE',
  DUPLICATE_EDGES = 'DUPLICATE_EDGES',
  EDGE_IS_PART_OF_LOOP = 'EDGE_IS_PART_OF_LOOP',
  ROUTER_HAS_MULTIPLE_SOURCE_NODES = 'ROUTER_HAS_MULTIPLE_SOURCE_NODES',
  ROUTER_MISSING_OUTGOING_EDGES = 'ROUTER_MISSING_OUTGOING_EDGES',
  ROUTER_CONNECTS_TO_ANOTHER_ROUTER = 'ROUTER_CONNECTS_TO_ANOTHER_ROUTER',
  ROUTER_ONLY_CONNECTED_TO_END_NODES = 'ROUTER_ONLY_CONNECTED_TO_END_NODES',
  CHAINED_ROUTER_LIMIT_EXCEEDED = 'CHAINED_ROUTER_LIMIT_EXCEEDED',
  CHAINED_ROUTERS_NOT_SUPPORTED = 'CHAINED_ROUTERS_NOT_SUPPORTED',
  ROUTER_EDGES_NOT_CONFIGURED = 'ROUTER_EDGES_NOT_CONFIGURED',
  DUPLICATE_PATHS_WITH_ROUTER = 'DUPLICATE_PATHS_WITH_ROUTER',
  MERGE_POINT_WITH_AN_AMBIGUOUS_BRANCH_POINT = 'MERGE_POINT_WITH_AN_AMBIGUOUS_BRANCH_POINT',
  ROUTER_HAS_MULTIPLE_PATHS_TO_SAME_SCHEMA_NODES = 'ROUTER_HAS_MULTIPLE_PATHS_TO_SAME_SCHEMA_NODES',
  MAPPERS_THROUGH_ROUTER_HAVE_DIFFERENT_SOURCE_TYPES = 'MAPPERS_THROUGH_ROUTER_HAVE_DIFFERENT_SOURCE_TYPES',
  MAPPERS_THROUGH_ROUTER_HAVE_DIFFERENT_SOURCES_THAN_ROUTER_RULES = 'MAPPERS_THROUGH_ROUTER_HAVE_DIFFERENT_SOURCES_THAN_ROUTER_RULES',
  LOOP_MISSING_TASK_OR_ROUTER_NODE = 'LOOP_MISSING_TASK_OR_ROUTER_NODE',
  LOOP_HAS_MULTIPLE_ENTRY_POINTS = 'LOOP_HAS_MULTIPLE_ENTRY_POINTS',
  MAX_NODES_LIMIT_EXCEEDED = 'MAX_NODES_LIMIT_EXCEEDED',
  MAX_EDGES_LIMIT_EXCEEDED = 'MAX_EDGES_LIMIT_EXCEEDED',
  UNKNOWN = 'UNKNOWN',
  UNNAMED_NODE = 'UNNAMED_NODE',
  DATA_FLOW_EDGE_WITH_NO_CONTROL_FLOW_EDGE = 'DATA_FLOW_EDGE_WITHOUT_CONTROL_FLOW_EDGE',
  DATA_FLOW_EDGE_SOURCE_TYPE_MISMATCH = 'DATA_FLOW_EDGE_SOURCE_TYPE_MISMATCH',
  DATA_FLOW_EDGE_MAPPING_FIELD_MISMATCH = 'DATA_FLOW_EDGE_MAPPING_FIELD_MISMATCH',
  DATA_FLOW_EDGE_TARGET_FIELD_COLLISION = 'DATA_FLOW_EDGE_TARGET_FIELD_COLLISION',
  CONTROL_FLOW_NO_DATA_FLOW_EDGE = 'CONTROL_FLOW_NO_DATA_FLOW_EDGE',
}

export interface ProcessFlowchartValidationError {
  // null for errors that are not associated with a specific object
  object: ProcessFlowchartErrorObject | null;
  parentObject?: ProcessFlowchartErrorObject | null;
  // Optional title message for rendering the error in the UI
  errorTitle?: string;
  errorMessage: string;
  errorType: ProcessFlowchartErrorType;
  // Indicator of whether the error blocks persistence. If it does not, it only blocks execution
  // For the time being, all errors should block persistence.
  // Over time, we can relax this for some rules.
  blocksPersistence: boolean;
}

export type MaybeValidationError = ProcessFlowchartValidationError | null;

export interface ProcessFlowchartValidationResult {
  errors: Array<ProcessFlowchartValidationError>;
}
